import { useQuery } from '@tanstack/react-query';

import type { TvDashboard } from 'src/api/zrm';
import type { TvDashboardSnapshot } from 'src/tv/dashboardTypes';
import { fetchTvDashboard } from 'src/tv/tvClient';
import { useFailingSince, useUnpairedOn } from 'src/tv/tvQueries';

export interface TvDashboardState {
  /** The last snapshot that arrived; kept through outages so the screen never goes blank. */
  snapshot: TvDashboardSnapshot | null;
  /** False until the first answer, so "loading" and "no data yet" can differ. */
  hasAnswered: boolean;
  /** When the current outage began, or null while online. */
  offlineSince: number | null;
}

/**
 * Polls `GET /tv/dashboard` with the TV QueryClient's defaults (`tvQueries.ts`): once a minute, the last
 * snapshot kept through failures, a 401 handed back to pairing.
 */
export const useTvDashboard = (dashboard: TvDashboard, token: string, onUnpaired: () => void): TvDashboardState => {
  const { data, error, failureCount } = useQuery({
    // The token is part of the key, so a re-paired screen never shows the previous pairing's numbers.
    queryKey: ['tv', 'dashboard', dashboard, token],
    // The signal aborts the request when the screen unmounts (a re-pair), so no answer for the old token lands.
    queryFn: ({ signal }) => fetchTvDashboard(dashboard, token, signal),
  });

  const offlineSince = useFailingSince(failureCount);

  useUnpairedOn(error, onUnpaired);

  return { snapshot: data ?? null, hasAnswered: data !== undefined, offlineSince };
};
